import React, { useState, useEffect } from "react";
import { Modal, DatePicker, Tabs, Select, Badge, Button, Divider, Typography, Row, Col, message, Input } from "antd";
import { EditOutlined, UserAddOutlined, TeamOutlined, } from "@ant-design/icons";
import { assignParticipants, quitarParticipante, getParticipantesAgregados, updateEca, getDepartamentos, getMunicipios, getSesionEcas, getProyectos } from "../../services/EcasParticipante/ecaParticipanteService";
import ParticipantSelector from "./ParticipantSelector";
import CurrentParticipants from "./CurrentParticipants";
import dayjs from "dayjs";

const { TabPane } = Tabs;
const { Title, Text } = Typography;
const { Option } = Select;

export default function ECAModal({ isOpen, onClose, eca, ecaEditando, onECAUpdated, user }) {
  const [nombreECA, setNombreECA] = useState("");
  const [estadoECA, setEstadoECA] = useState("activa");
  const [departamento, setDepartamento] = useState(null);
  const [municipio, setMunicipio] = useState(null);
  const [departamentos, setDepartamentos] = useState([]);
  const [municipios, setMunicipios] = useState([]);
  const [tipoEstado, setTipoEstado] = useState(eca.tipo_estado || null);
  const [fechaRegistro, setFechaRegistro] = useState(dayjs());
  const [participantesActuales, setParticipantesActuales] = useState([]);
  const [participantesDisponibles, setParticipantesDisponibles] = useState([]);
  const [totalNuevosDisponibles, setTotalNuevosDisponibles] = useState(0);
  const [activeTab, setActiveTab] = useState("datos");
  const [dataEcas, setDataEcas] = useState([]);
  const [oidNombreECA, setOidNombreECA] = useState(null);
  const [conformaciones, setConformaciones] = useState("");
  const estadoTextoMap = { 1: "activo", 2: "inactivo", 3: "eliminado", };
  const [proyectos, setProyectos] = useState([]);
  const [idProyecto, setIdProyecto] = useState(null);

  const cargarParticipantesActuales = async () => {
    try {
      const asignados = await getParticipantesAgregados(eca.oid);
      setParticipantesActuales(asignados);
    } catch (error) {
      console.error("Error cargando participantes asignados", error);
      setParticipantesActuales([]);
    }
  };

  const cargarProyectos = async () => {
    try {
      const res = await getProyectos();
      setProyectos(res);
    } catch (error) {
      console.error("Error al cargar proyectos:", error);
    }
  };

  useEffect(() => {
    const cargarInfoInicial = async () => {
      try {
        const [deps, muns] = await Promise.all([
          getDepartamentos(),
          getMunicipios(),
        ]);
        setDepartamentos(deps);
        setMunicipios(muns);
      } catch (err) {
        console.error("Error cargando deps/munis", err);
      }
    };

    if (eca && isOpen) {
      setNombreECA(eca.nombre);
      setEstadoECA(estadoTextoMap[eca.estado] || "activo");
      setDepartamento(eca.departamento);
      setMunicipio(eca.municipio);
      setConformaciones(eca.conformaciones || "");
      setIdProyecto(eca.id_proyecto); // 👈 PRECARGAR PROYECTO
      cargarProyectos();
      setParticipantesDisponibles([]);
      setTotalNuevosDisponibles(0);
      cargarEcas(); // Esta función cargará dataEcas
      setActiveTab("datos");
      cargarInfoInicial();
      cargarParticipantesActuales();

      const parsedTipoEstado = typeof eca.tipo_estado === "string"
        ? eca.tipo_estado.split(",").map((id) => parseInt(id.trim()))
        : Array.isArray(eca.tipo_estado)
          ? eca.tipo_estado
          : [];

      setTipoEstado(parsedTipoEstado);

      // 👇 AÑADIR ESTA PARTE para precargar `oidNombreECA`
      if (dataEcas.length > 0 && eca.nombre) {
        const sesionMatch = dataEcas.find((s) => s.nombre_eca === eca.nombre);
        if (sesionMatch) {
          setOidNombreECA(sesionMatch.oid);
        }
      }
    }
  }, [eca, isOpen]);


  const cargarEcas = async () => {
    try {
      const res = await getSesionEcas();
      setDataEcas(res);
      // Asociar automáticamente si `eca.nombre` existe
      if (eca?.nombre) {
        const sesion = res.find((s) => s.nombre_eca === eca.nombre);
        if (sesion) {
          setOidNombreECA(sesion.oid);
        }
      }
    } catch (error) {
      console.error("Error al cargar sesiones ECA:", error);
    }
  };

  const handleGuardarCambios = async () => {
    if (!eca || !nombreECA.trim() || !departamento || !municipio) {
      return message.warning("Por favor completa todos los campos requeridos");
    }

    const estadosMap = {
      activo: 1,
      inactivo: 2,
      eliminado: 3,
    };

    const ecaActualizada = {
      oidnombreeca: oidNombreECA,
      nombreeca: nombreECA.trim(),
      observacion: "",
      userupdate: user,
      departamento,
      municipio,
      fecha_registro: fechaRegistro.format("YYYY-MM-DD"),
      estado: estadosMap[estadoECA.toLowerCase().trim()],
      tipo_estado: tipoEstado.join(","),
      conformaciones: conformaciones.trim(),
      id_proyecto: idProyecto,
      oid: eca.oid,
    };

    try {
      await updateEca(eca.oid, ecaActualizada);
      const proyectoObj = proyectos.find(p => p.oid === idProyecto);
      onECAUpdated({
        ...eca,
        nombre: nombreECA,
        estado: estadosMap[estadoECA.toLowerCase().trim()],
        participantes: participantesActuales.length,
        departamento,
        municipio,
        conformaciones: conformaciones.trim(),
        id_proyecto: idProyecto,
        codigo_proyecto: proyectoObj?.codigo || "",
        proyecto: `${proyectoObj?.codigo || ""} ${proyectoObj?.proyecto || ""}`,
      });
      message.success("Cambios guardados correctamente");
      handleClose();
    } catch (error) {
      console.error("Error al actualizar ECA", error);
      message.error("No se pudo actualizar la ECA");
    }
  };

  const handleClose = () => {
    if (participantesDisponibles.length > 0) {
      Modal.confirm({
        title: "Tienes cambios sin guardar",
        content:
          "Has seleccionado participantes que aún no has agregado. ¿Estás seguro de que deseas cerrar sin guardarlos?",
        okText: "Cerrar sin guardar",
        cancelText: "Volver",
        onOk: () => {
          resetearModal();
        },
      });
    } else {
      resetearModal();
    }
  };

  const resetearModal = () => {
    setNombreECA("");
    setEstadoECA("activa");
    setParticipantesActuales([]);
    setParticipantesDisponibles([]);
    setTotalNuevosDisponibles(0);
    setDepartamento(null);
    setMunicipio(null);
    setActiveTab("datos");
    onClose();
  };

  const handleAgregarParticipantes = async (nuevos) => {
    try {
      const nuevosIds = nuevos.map((p) => p.oid);
      await assignParticipants(eca.oid, nuevosIds);
      setParticipantesActuales((prev) => [...prev, ...nuevos]);
      setParticipantesDisponibles([]);
      message.success(`${nuevos.length} participante(s) agregado(s)`);
    } catch (error) {
      console.error(error);
      message.error("Error al agregar participantes");
    }
  };

  const handleQuitarParticipante = async (id) => {
    try {
      await quitarParticipante(eca.oid, id);
      const removido = participantesActuales.find((p) => p.oid === id);
      if (removido) {
        setParticipantesActuales((prev) =>
          prev.filter((p) => p.oid !== id)
        );
      }
      message.success("Participante removido");
    } catch (error) {
      console.error(error);
      message.error("Error al quitar participante");
    }
  };

  if (!eca) return null;

  return (
    <Modal
      title={<span><EditOutlined /> Editar Sesión de ECA: {nombreECA}</span>}
      open={isOpen}
      onCancel={handleClose}
      width={900}
      styles={{ body: { maxHeight: "75vh" } }}
      footer={[
        <Button key="cancel" onClick={handleClose}>
          Cancelar
        </Button>,
        participantesDisponibles.length > 0 && (
          <Button
            key="add"
            icon={<UserAddOutlined />}
            onClick={() => handleAgregarParticipantes(participantesDisponibles)}
            style={{
              backgroundColor: "#008000",
              color: "#ffffff",
            }}
          >
            Agregar {participantesDisponibles.length} Participante(s)
          </Button>
        ),
        <Button
          key="submit"
          type="primary"
          onClick={handleGuardarCambios}
          disabled={
            !nombreECA ||
            !departamento ||
            !municipio ||
            !fechaRegistro ||
            !tipoEstado ||
            participantesDisponibles.length > 0
          }
        >
          Guardar Cambios
        </Button>

      ]}
    >
      <Text type="secondary">
        Modifica los datos de la sesión de la ECA y gestiona sus participantes.
      </Text>
      <Divider />
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab="Datos Básicos" key="datos" style={{ marginBottom: "10px" }}>
          <Row gutter={16}>
            <Col span={24}>
              <label>Nombre de la Sesión ECA</label>
              <Select
                placeholder="Seleccionar Nombre de la Sesión ECA"
                value={oidNombreECA}
                onChange={(oidSeleccionado) => {
                  const seleccionada = dataEcas.find(e => e.oid === oidSeleccionado);
                  setOidNombreECA(oidSeleccionado);
                  setNombreECA(seleccionada?.nombre_eca || "");
                }}
                style={{ width: "100%", marginBottom: 15 }}
              >
                {dataEcas.map((eca) => (
                  <Option key={eca.oid} value={eca.oid}>{eca.nombre_eca}</Option>
                ))}
              </Select>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <label>Tipo de Temática</label>
              <Select
                mode="multiple"
                value={tipoEstado}
                onChange={setTipoEstado}
                style={{ width: "100%", marginBottom: 15 }}
                placeholder="Seleccionar Tipo de Temática"
              >
                <Option value={1}>Técnico</Option>
                <Option value={2}>Ambiental</Option>
                <Option value={3}>Socioempresarial</Option>
                <Option value={4}>Comercial</Option>
                <Option value={5}>DHAA</Option>
                <Option value={6}>Enfoque Diferencial</Option>
              </Select>
            </Col>
            <Col span={12}>
              <label>Fecha sesión ECA</label>
              <DatePicker
                disabled={true}
                placeholder="Seleccionar Fecha sesión ECA"
                value={fechaRegistro}
                onChange={(date) => setFechaRegistro(date)}
                format="YYYY-MM-DD"
                style={{ width: "100%", marginBottom: 15 }}
              />
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <label>Departamento</label>
              <Select
                value={departamento}
                disabled={true}
                onChange={(value) => {
                  setDepartamento(value);
                  setMunicipio(null);
                }}
                placeholder="Seleccionar departamento"
                style={{ width: "100%", marginBottom: 15 }}
              >
                {departamentos.map((dep) => (
                  <Option key={dep.oid} value={dep.oid}>
                    {dep.departamento}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col span={12}>
              <label>Municipio</label>
              <Select
                value={municipio}
                disabled={true}
                onChange={setMunicipio}
                placeholder="Seleccionar municipio"
                style={{ width: "100%", marginBottom: 15 }}
              >
                {municipios
                  .filter((m) => m.oid_depto === departamento)
                  .map((mun) => (
                    <Option key={mun.oid} value={mun.oid}>
                      {mun.municipio}
                    </Option>
                  ))}
              </Select>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <label>Proyecto</label>
              <Select
                placeholder="Seleccionar Proyecto"
                value={idProyecto}
                onChange={setIdProyecto}
                style={{ width: "100%", marginBottom: 15 }}
                showSearch
                optionFilterProp="children"
              >
                {proyectos.map((p) => (
                  <Option key={p.oid} value={p.oid}>
                    {p.codigo} {p.proyecto}
                  </Option>
                ))}
              </Select>
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <label>Conformaciones</label>
              <Input.TextArea
                placeholder="Escribe la conformación de la sesión"
                value={conformaciones}
                onChange={(e) => setConformaciones(e.target.value)}
                rows={2}
                style={{ width: "100%", marginBottom: 15 }}
              />
            </Col>
          </Row>
          <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
            <Col xs={24} md={8} style={{ textAlign: "center" }}>
              <Title level={4}>{participantesActuales.length}</Title>
              <Text type="secondary">Participantes Actuales</Text>
            </Col>
            <Col xs={24} md={8} style={{ textAlign: "center" }}>
              <Title level={4} style={{ color: "green" }}>
                {participantesDisponibles.length}
              </Title>
              <Text type="secondary">Seleccionados para Agregar</Text>
            </Col>
            <Col xs={24} md={8} style={{ textAlign: "center" }}>
              <Title level={4}>{totalNuevosDisponibles}</Title>
              <Text type="secondary">Nuevos Disponibles</Text>
            </Col>
          </Row>
        </TabPane>

        <TabPane
          tab={
            <span>
              <UserAddOutlined /> Agregar Participantes{" "}
              {participantesDisponibles.length > 0 && (
                <Badge count={participantesDisponibles.length} offset={[5, -5]} />
              )}
            </span>
          }
          key="agregar"
        >
          <Title level={5}>Agregar Nuevos Participantes</Title>
          <Text type="secondary">
            Selecciona participantes para agregar a esta sesión ECA.
          </Text>
          <div style={{ margin: "16px 0" }}>
            <ParticipantSelector
              ecaOid={eca.oid}
              participantesSeleccionados={participantesDisponibles}
              onParticipantesChange={setParticipantesDisponibles}
              participantesExcluidos={participantesActuales}
              onCantidadParticipantesFiltrados={setTotalNuevosDisponibles}
              filtroDepartamentoInicial={departamento}
              filtroMunicipioInicial={municipio}
            />
          </div>
        </TabPane>
        <TabPane
          tab={
            <span>
              <TeamOutlined /> Participantes Actuales{" "}
              <Badge count={participantesActuales.length} offset={[5, -5]} />
            </span>
          }
          key="actuales"
        >
          <Title level={5}>Participantes Actuales</Title>
          <Text type="secondary">
            Gestiona los participantes que ya están en esta sesión de ECA.
          </Text>
          <div style={{ marginTop: 16 }}>
            <CurrentParticipants
              ecaOid={eca.oid}
              participantesActuales={participantesActuales}
              onQuitarParticipante={handleQuitarParticipante}
            />
          </div>
        </TabPane>
      </Tabs>
    </Modal>
  );
}
